//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  IAdobeAuthenticationCredentials,
  IGetAdobeSettingsResponse,
  IGetCuralatePixelSettingsResponse,
  IGetGA4BigQueryDatasetResponse,
  IGetGA4ResponseMetadata,
  IGetGA4SettingsResponse,
  IGetGASettingsResponse,
} from '@apis/analytics-settings/analyticsSettingsApiModel';
import GaProfileId from '@models/GaProfileId';
import {
  serializedGatewayFetch,
  serializedSessionFetch,
} from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const BASE_URL = '/api/settings';
const GA_URL = `/ga`;
const GA4_URL = `/ga4`;

function settingsFetch(endpoint: string, init?: RequestInit) {
  return serializedSessionFetch(`${BASE_URL}${endpoint}`, init);
}

const webGatewayAnalyticsProvidersFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v1/settings/analytics-providers${endpoint}`,
    init,
  );
};

/**
 * Gets the current client's Google Analytics settings
 *
 * GET /api/settings/ga
 */
export const getGASettings = async (
  init?: RequestInit,
): Promise<IGetGASettingsResponse> => {
  const res = await settingsFetch(GA_URL, init);
  return await res.json<IGetGASettingsResponse>();
};

/**
 * Gets the current client's Google Analytics settings
 *
 * GET /api/settings/ga4
 */
export const getGA4Settings = async (
  init?: RequestInit,
): Promise<IGetGA4SettingsResponse> => {
  const res = await settingsFetch(GA4_URL, init);
  return await res.json<IGetGA4SettingsResponse>();
};

/**
 * Gets the current client's Google BigQuery settings
 *
 * GET /api/settings/ga4/bigquery
 */
export const getGA4BigQueryDataset =
  async (): Promise<IGetGA4BigQueryDatasetResponse> => {
    const res = await settingsFetch(`${GA4_URL}/bigquery`);
    return res.json();
  };

export const getGA4ResponseMetadata = async (
  profileId: GaProfileId,
): Promise<IGetGA4ResponseMetadata[]> => {
  const res = await settingsFetch(
    `${GA4_URL}/metadata/${encodeURIComponent(profileId)}`,
  );
  const json: { data: IGetGA4ResponseMetadata[] } = await res.json();
  return json.data;
};

/**
 * Delete the client's Google BigQuery settings
 *
 * DELETE /api/settings/ga4/bigquery
 */
export const deleteGA4BigQueryDataset = async (): Promise<void> => {
  await settingsFetch(`${GA4_URL}/bigquery`, { method: 'DELETE' });
};

/**
 * Create/Update the current client's Google BigQuery settings
 *
 * POST /api/settings/ga4/analytics-hub
 */
export const subscribeAnalyticsHubListing = async (
  url: string,
): Promise<void> => {
  await settingsFetch(`${GA4_URL}/analytics-hub`, {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
};

/**
 * Gets the current client's Curalate pixel info.
 *
 * GET /api/settings/pixel
 */
export const getCuralatePixelSettings = async (
  init?: RequestInit,
): Promise<IGetCuralatePixelSettingsResponse> => {
  const res = await settingsFetch('/pixel', init);
  return await res.json<IGetCuralatePixelSettingsResponse>();
};

/**
 * Gets the Google Analytics oauth url
 *
 * GET /api/settings/ga
 */
export const getGAOAuthURL = async (): Promise<{ url?: string }> => {
  const res = await settingsFetch(`${GA_URL}/oauth`);
  return await res.json();
};

/**
 * disconnects the clients Google Analytics account
 *
 * DELETE /api/settings/ga/deauth
 */
export const deauthGAAccount = async (): Promise<void> => {
  await settingsFetch(`${GA_URL}/deauth`, {
    method: 'DELETE',
  });
};

/**
 * Updates the clients Google Analytics profile
 *
 * PUT /api/settings/ga/profile/${profileId}
 */
export const updateGAProfile = async (
  profileId: GaProfileId,
): Promise<void> => {
  await settingsFetch(`${GA_URL}/profile/${profileId}`, {
    method: 'PUT',
  });
};

/**
 * Gets the current client's Adobe Analytics settings (web-gateway)
 */
export async function getAdobeWebServiceSettings(
  init?: RequestInit,
): Promise<IGetAdobeSettingsResponse> {
  const res = await webGatewayAnalyticsProvidersFetch(
    '/adobe/web-service',
    init,
  );

  return await res.json<IGetAdobeSettingsResponse>();
}

export async function getAdobeOAuthSettings(
  init?: RequestInit,
): Promise<IGetAdobeSettingsResponse> {
  const res = await webGatewayAnalyticsProvidersFetch('/adobe/oauth', init);

  return await res.json<IGetAdobeSettingsResponse>();
}

export async function connectAdobeAnalyticsOAuth(
  request: IAdobeAuthenticationCredentials,
): Promise<void> {
  await webGatewayAnalyticsProvidersFetch('/adobe/oauth/account', {
    body: JSON.stringify(request),
    method: 'POST',
  });
}

/**
 * Disconnects a clients adobe analytics connection (web-gateway)
 */
export async function disconnectAdobeAnalyticsWebservice(): Promise<void> {
  await webGatewayAnalyticsProvidersFetch('/adobe/web-service/account', {
    method: 'DELETE',
  });
}

export async function disconnectAdobeAnalyticsOAuth(): Promise<void> {
  await webGatewayAnalyticsProvidersFetch('/adobe/oauth/account', {
    method: 'DELETE',
  });
}
